import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Button, Card, Container, Form, Spinner, Table } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import Header from '../layouts/Header'
import Footer from '../layouts/Footer'
import axiosInstance from '../services/axiosInterceptor'
import useSchoolSettings from '../hooks/useSchoolSettings'
import { beYearOptions, currentBeYear, fmtBaht } from './aoShared'

// สรุปผลการดำเนินงานโครงการประจำปี (ต่อฝ่าย) + พิมพ์
export default function AnnualSummary() {
  const school = useSchoolSettings()
  const [year, setYear] = useState(String(currentBeYear()))
  const [summary, setSummary] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const res = await axiosInstance.get(`/api/ao/projects/annual-summary?year=${year}`)
      if (res.data.success) setSummary(res.data.data.summary)
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดข้อมูลไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [year])

  useEffect(() => { fetchData() }, [fetchData])

  const totals = summary.reduce((acc, r) => ({
    projects: acc.projects + Number(r.project_count),
    budget: acc.budget + Number(r.total_budget || 0),
    spent: acc.spent + Number(r.total_spent || 0),
    done: acc.done + Number(r.done_count),
  }), { projects: 0, budget: 0, spent: 0, done: 0 })

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <Container fluid>
          <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2 d-print-none">
            <h4 className="main-title mb-0">
              <Link to="/admin-office/projects" className="text-decoration-none me-2">
                <i className="ri-arrow-left-line" />
              </Link>
              <i className="ri-file-chart-line me-2 text-primary" />
              สรุปผลการดำเนินงานประจำปี
            </h4>
            <div className="d-flex gap-2">
              <Form.Select size="sm" style={{ width: 120 }} value={year} onChange={e => setYear(e.target.value)}>
                {beYearOptions().map(y => <option key={y} value={y}>พ.ศ. {y}</option>)}
              </Form.Select>
              <Button variant="primary" size="sm" onClick={() => window.print()}>
                <i className="ri-printer-line me-1" />พิมพ์
              </Button>
            </div>
          </div>

          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
          ) : (
            <Card className="card-one">
              <Card.Body>
                <div className="text-center mb-3">
                  <h5 className="mb-0">สรุปผลการดำเนินงานโครงการ ประจำปี พ.ศ. {year}</h5>
                  <div className="text-muted fs-sm">{school.school_name}</div>
                </div>
                {summary.length === 0 ? (
                  <div className="text-center text-muted py-4">ไม่มีโครงการในปีนี้</div>
                ) : (
                  <Table bordered responsive className="fs-sm align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>ฝ่าย/กลุ่มงาน</th>
                        <th className="text-center">จำนวนโครงการ</th>
                        <th className="text-end">งบประมาณ (บาท)</th>
                        <th className="text-end">เบิกจ่าย (บาท)</th>
                        <th className="text-center">เสร็จสิ้น</th>
                        <th className="text-center">กำลังดำเนินการ</th>
                        <th className="text-center">รอดำเนินการ</th>
                        <th className="text-center">ยกเลิก</th>
                        <th className="text-center">คะแนนประเมินเฉลี่ย</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.map(r => (
                        <tr key={r.department}>
                          <td>{r.department}</td>
                          <td className="text-center">{r.project_count}</td>
                          <td className="text-end">{fmtBaht(r.total_budget)}</td>
                          <td className="text-end">{fmtBaht(r.total_spent)}</td>
                          <td className="text-center">{r.done_count}</td>
                          <td className="text-center">{r.in_progress_count}</td>
                          <td className="text-center">{r.planned_count}</td>
                          <td className="text-center">{r.cancelled_count}</td>
                          <td className="text-center">{r.avg_eval_score ? Number(r.avg_eval_score).toFixed(2) : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="table-light fw-semibold">
                      <tr>
                        <td>รวมทั้งสิ้น</td>
                        <td className="text-center">{totals.projects}</td>
                        <td className="text-end">{fmtBaht(totals.budget)}</td>
                        <td className="text-end">{fmtBaht(totals.spent)}</td>
                        <td className="text-center">{totals.done}</td>
                        <td className="text-center" colSpan={4}>
                          อัตราเบิกจ่าย {totals.budget > 0 ? `${(totals.spent / totals.budget * 100).toFixed(1)}%` : '-'}
                        </td>
                      </tr>
                    </tfoot>
                  </Table>
                )}
              </Card.Body>
            </Card>
          )}
        </Container>
      </div>
      <Footer />
    </>
  )
}
